import type React from 'react';
import { NamesStep } from '@/pages/catalogImport/names';
import { ProductsStep } from '@/pages/catalogImport/products';
import { ResultStep } from '@/pages/catalogImport/result';
import { UploadStep } from '@/pages/catalogImport/upload';
import { PATH } from '@/router/PATH';
import { ChosenBranch, OnboardingStepStrip, WizardFrame } from './components';
import {
    AccountingStartStep,
    BranchStep,
    DoneStep,
    WelcomeStep,
} from './steps';
import type { UseOnboardingPageResult } from './useOnboardingPage';

type OnboardingPageViewProps = Omit<UseOnboardingPageResult, 'isBarePath'>;

/**
 * The wizard: the frame and its strip around the current step's body. A
 * segment that names no step renders an empty body.
 *
 * Every Back opens its target by path (bos-app#201). The wizard's own steps
 * decide theirs, as they decide where they go on: Accounting → Welcome;
 * Branch → Accounting while the organisation still needs it, else Welcome.
 * The import steps, shared with the in-shell page, take theirs from here:
 * File → Branch, Names → File, Products → Names. Welcome, Result and Done
 * have none. Accounting's year lives in the page (`accountingYear`), so it
 * survives a trip to Welcome.
 *
 * The strip's links back (`OnboardingStepStrip`) open their steps by path
 * too; it renders here, inside the page's import store, which it reads.
 */
export const OnboardingPageView: React.FC<OnboardingPageViewProps> = ({
    step,
    columnWidth,
    showsBranchContext,
    accountingNeeded,
    accountingYear,
    onAccountingYearChange,
    organisationName,
    isSigningOut,
    onSignOut,
}) => (
    <WizardFrame
        columnWidth={columnWidth}
        context={showsBranchContext ? <ChosenBranch /> : undefined}
        isSigningOut={isSigningOut}
        onSignOut={onSignOut}
        organisationName={organisationName}
        strip={
            <OnboardingStepStrip
                accountingNeeded={accountingNeeded}
                currentStep={step}
            />
        }
    >
        {step === 'start' ? <WelcomeStep /> : null}
        {step === 'accounting' ? (
            <AccountingStartStep
                onYearChange={onAccountingYearChange}
                year={accountingYear}
            />
        ) : null}
        {step === 'branch' ? <BranchStep /> : null}
        {step === 'file' ? (
            <UploadStep
                backPath={PATH.ONBOARDING_STEP.BIND('branch')}
                mode="wizard"
                nextPath={PATH.ONBOARDING_STEP.BIND('names')}
            />
        ) : null}
        {step === 'names' ? (
            <NamesStep
                backPath={PATH.ONBOARDING_STEP.BIND('file')}
                nextPath={PATH.ONBOARDING_STEP.BIND('products')}
            />
        ) : null}
        {step === 'products' ? (
            <ProductsStep
                backPath={PATH.ONBOARDING_STEP.BIND('names')}
                nextPath={PATH.ONBOARDING_STEP.BIND('result')}
                showUndoNote
            />
        ) : null}
        {step === 'result' ? (
            <ResultStep
                anotherFilePath={PATH.ONBOARDING_STEP.BIND('branch')}
                backPath={PATH.ONBOARDING_STEP.BIND('products')}
                mode="wizard"
                nextPath={PATH.ONBOARDING_STEP.BIND('done')}
                showUndo
            />
        ) : null}
        {step === 'done' ? <DoneStep /> : null}
    </WizardFrame>
);
